import React, { useState, useMemo, useRef } from 'react';
import { Box, Chip, Popover, Typography, IconButton, Tooltip, useMediaQuery } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import TuneIcon from '@mui/icons-material/Tune';
import PeriodRangePopover from './PeriodRangePopover';
import { ControlSlider } from '../common/controls';
import { CONTROL_SLIDER_SIZES, getControlTokens } from '../common/controls/controlTokens';
import {
  getAvailableYears,
  createLastXFilter,
  createYearFilter,
  createRangeFilter,
  isLastXFilter,
  isYearFilter,
  isRangeFilter,
} from '../../utils/periodFilters';

/**
 * EnhancedSlider - A slider with preset dropdown for quick period selection
 *
 * @param {object} props
 * @param {string} props.period - "Quarters" | "Years"
 * @param {number} props.sliderValue - Current slider value
 * @param {number} props.maxSliderValue - Maximum slider value
 * @param {function} props.handleSliderChange - Slider change handler
 * @param {function} props.handleInputChange - Input change handler
 * @param {function} props.handleBlur - Input blur handler
 * @param {Array<{period: string, fy?: string, endDate?: string}>} props.periods - Array of period objects
 * @param {{type: string, value?: number|string, start?: string, end?: string}} props.periodFilter - Current filter state
 * @param {function} props.onFilterChange - Callback when filter changes: (filter) => void
 * @param {boolean} props.showPresets - Whether to show preset dropdown (default: true)
 */
const EnhancedSlider = ({
  context = 'filterBar',
  period = 'Quarters',
  sliderValue,
  maxSliderValue,
  handleSliderChange,
  handleInputChange,
  handleBlur,
  periods = [],
  periodFilter,
  onFilterChange,
  showPresets = true,
  width = CONTROL_SLIDER_SIZES.width,
  presetButtonWidth = CONTROL_SLIDER_SIZES.presetButtonWidth,
  presetButtonOffset = CONTROL_SLIDER_SIZES.presetButtonOffset,
}) => {
  const theme = useTheme();
  const tokens = getControlTokens(theme, context);
  const isMobile = useMediaQuery('(max-width:480px)');

  // Ref for the presets button (used as stable anchor for range popover)
  const presetsButtonRef = useRef(null);

  // Popover state for presets dropdown
  const [presetsAnchorEl, setPresetsAnchorEl] = useState(null);
  const presetsOpen = Boolean(presetsAnchorEl);

  // Popover state for custom range
  const [rangePopoverOpen, setRangePopoverOpen] = useState(false);

  // Get available years from period data
  const availableYears = useMemo(() => {
    return getAvailableYears(periods);
  }, [periods]);

  // Build preset chips configuration
  const presetChips = useMemo(() => {
    const chips = [];
    const isQuarterly = period === 'Quarters';

    // Last X presets
    if (isQuarterly) {
      chips.push({ label: 'Last 4', filter: createLastXFilter(4), type: 'lastX', value: 4 });
      chips.push({ label: 'Last 8', filter: createLastXFilter(8), type: 'lastX', value: 8 });
      chips.push({ label: 'Last 12', filter: createLastXFilter(12), type: 'lastX', value: 12 });
    } else {
      chips.push({ label: 'Last 3', filter: createLastXFilter(3), type: 'lastX', value: 3 });
      chips.push({ label: 'Last 5', filter: createLastXFilter(5), type: 'lastX', value: 5 });
      chips.push({ label: 'Last 10', filter: createLastXFilter(10), type: 'lastX', value: 10 });
    }

    // Year presets (show up to 3 recent years in dropdown)
    availableYears.slice(0, 3).forEach(year => {
      const fullYear = parseInt(year, 10) < 100 ? `20${year}` : year;
      chips.push({
        label: fullYear,
        filter: createYearFilter(year),
        type: 'year',
        value: year,
      });
    });

    // Custom range option
    chips.push({ label: 'Custom Range...', isCustom: true });

    return chips;
  }, [period, availableYears]);

  // Handle preset chip click
  const handlePresetClick = (chip) => {
    if (chip.isCustom) {
      setPresetsAnchorEl(null); // Close presets popover
      setRangePopoverOpen(true); // Open range popover using button ref as anchor
      return;
    }

    if (onFilterChange) {
      onFilterChange(chip.filter);
    }

    // Also update slider value for lastX filters to keep them in sync
    if (chip.type === 'lastX' && handleSliderChange) {
      handleSliderChange(null, chip.value);
    }

    // Close the popover after selection
    setPresetsAnchorEl(null);
  };

  // Handle custom range apply
  const handleRangeApply = ({ start, end }) => {
    if (onFilterChange) {
      onFilterChange(createRangeFilter(start, end));
    }
    setRangePopoverOpen(false);
  };

  // Handle slider change - update filter to lastX mode
  const handleSliderChangeInternal = (event, newValue) => {
    if (handleSliderChange) {
      handleSliderChange(event, newValue);
    }
    if (onFilterChange) {
      onFilterChange(createLastXFilter(newValue));
    }
  };

  // Check if a chip is selected
  const isChipSelected = (chip) => {
    if (!periodFilter) return false;

    if (chip.isCustom) {
      return isRangeFilter(periodFilter);
    }

    if (chip.type === 'lastX') {
      return isLastXFilter(periodFilter, chip.value);
    }

    if (chip.type === 'year') {
      return isYearFilter(periodFilter, chip.value);
    }

    return false;
  };

  // Check if any non-default filter is active (for icon highlighting)
  const hasActivePreset = useMemo(() => {
    if (!periodFilter) return false;
    // Consider it "active" if it's a year filter or range filter
    return periodFilter.type === 'year' || periodFilter.type === 'range';
  }, [periodFilter]);

  // Get label for custom chip when range is selected
  const getCustomChipLabel = (chip) => {
    if (chip.isCustom && isRangeFilter(periodFilter)) {
      return `${periodFilter.start} - ${periodFilter.end}`;
    }
    return chip.label;
  };

  // Chip styles
  const getChipSx = (isSelected) => ({
    height: '26px',
    fontSize: '11px',
    fontWeight: isSelected ? 600 : 400,
    backgroundColor: isSelected
      ? theme.palette.mode === 'dark'
        ? 'rgba(100, 150, 220, 0.3)'
        : 'rgba(40, 100, 180, 0.15)'
      : theme.tableHeader.highlight,
    color: isSelected ? theme.palette.primary.main : theme.palette.text.secondary,
    border: isSelected
      ? `1px solid ${theme.palette.primary.main}`
      : `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)'}`,
    '&:hover': {
      backgroundColor: isSelected
        ? theme.palette.mode === 'dark'
          ? 'rgba(100, 150, 220, 0.4)'
          : 'rgba(40, 100, 180, 0.25)'
        : theme.palette.action.hover,
    },
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  });

  // Don't show presets on mobile
  const shouldShowPresets = showPresets && !isMobile && periods.length > 0;
  const combinedWidth = shouldShowPresets
    && typeof width === 'number'
    && typeof presetButtonWidth === 'number'
    && typeof presetButtonOffset === 'number'
    ? `${width + presetButtonWidth + presetButtonOffset}px`
    : 'fit-content';

  const selectBorderColor =
    theme.palette.mode === 'light'
      ? alpha(theme.palette.common.black, 0.23)
      : alpha(theme.palette.common.white, 0.23);
  const label = period === 'Years' ? 'Years' : period === 'Weeks' ? 'Weeks' : period === 'Months' ? 'Months' : 'Quarters';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 0,
        width: combinedWidth,
        maxWidth: '100%',
      }}
    >
      <ControlSlider
        context={context}
        labelLines={['Display Last', `${sliderValue} ${label}`]}
        value={sliderValue}
        max={maxSliderValue}
        min={2}
        onChange={handleSliderChangeInternal}
        onInputChange={handleInputChange}
        onBlur={handleBlur}
        width={width}
        inputAriaLabel={`${label} displayed`}
      />

      {/* Presets dropdown button */}
      {shouldShowPresets && (
        <Tooltip title="Period Presets">
          <IconButton
            ref={presetsButtonRef}
            size="small"
            onClick={(e) => setPresetsAnchorEl(e.currentTarget)}
            sx={{
              ml: typeof presetButtonOffset === 'number' ? `${presetButtonOffset}px` : presetButtonOffset,
              height: tokens.height,
              width: presetButtonWidth,
              minWidth: presetButtonWidth,
              backgroundColor: tokens.surface,
              border: `1px solid ${selectBorderColor}`,
              borderRadius: `${tokens.radius}px`,
              color: hasActivePreset ? theme.palette.primary.main : theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <TuneIcon sx={{ fontSize: tokens.iconSize }} />
          </IconButton>
        </Tooltip>
      )}

      {/* Presets popover */}
      <Popover
        open={presetsOpen}
        anchorEl={presetsAnchorEl}
        onClose={() => setPresetsAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            p: 1.5,
            minWidth: 180,
            backgroundColor: theme.background?.transparent || theme.palette.background.paper,
            backdropFilter: 'blur(8px)',
            borderRadius: '8px',
          },
        }}
      >
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mb: 1,
            color: theme.palette.text.secondary,
            fontSize: '10px',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          Quick Select
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
          }}
        >
          {presetChips.map((chip, index) => {
            const isSelected = isChipSelected(chip);
            return (
              <Chip
                key={chip.label + index}
                label={getCustomChipLabel(chip)}
                size="small"
                onClick={() => handlePresetClick(chip)}
                sx={{
                  ...getChipSx(isSelected),
                  justifyContent: 'flex-start',
                  width: '100%',
                }}
              />
            );
          })}
        </Box>
      </Popover>

      {/* Custom range popover */}
      <PeriodRangePopover
        anchorEl={presetsButtonRef.current}
        open={rangePopoverOpen}
        onClose={() => setRangePopoverOpen(false)}
        periods={periods}
        onApply={handleRangeApply}
        initialStart={periodFilter?.type === 'range' ? periodFilter.start : undefined}
        initialEnd={periodFilter?.type === 'range' ? periodFilter.end : undefined}
      />
    </Box>
  );
};

export default EnhancedSlider;
